"use client"

import dynamic from "next/dynamic"
import { useMemo, useRef, useEffect, useState } from "react"
import { PropertiesList } from "@/components/organisms/HouseList"
import { PaginationContainer } from "@/components/organisms/PaginationContainer/pagination-container"
import { Placeholder } from "@/components/atoms/Placeholder/placeholder"
import { ItemNotFound } from "@/components/molecules/NotFound"
import { usePropertySearch } from "@/hooks/usePropertySearch"
import LoadingGear from "@/components/molecules/Loader"
import { House } from "@/app/types"

// Dynamic map with client-only rendering
const DynamicMap = dynamic(
  () => import("@/components/molecules/Map").then((m) => m.default),
  { ssr: false, loading: () => <Placeholder /> }
)

export const PropertiesListSection = () => {
  const { properties, totalPages, isLoading, currentPage } = usePropertySearch()
  const listRef = useRef<HTMLDivElement>(null)
  const [mapHeight, setMapHeight] = useState<number>(0)

  // Extract coordinates for the map
  const propertyCoordinates = useMemo(
    () =>
      properties
        .filter((property: House) => property.location?.coordinates)
        .map((property: House) => property.location.coordinates),
    [properties]
  )

  // Dynamically update map height based on the list container
  useEffect(() => {
    if (!listRef.current) return

    const updateHeight = () => {
      setMapHeight(listRef.current?.offsetHeight || 500)
    }

    updateHeight()
    window.addEventListener("resize", updateHeight)
    return () => window.removeEventListener("resize", updateHeight)
  }, [properties])

  if (isLoading) return <LoadingGear />

  return (
    <section className="relative flex min-h-screen lg:border-t lg:dark:border-t-gray-900">
      {/* Property List */}
      <div ref={listRef} className="flex-1 mx-auto w-full lg:ml-36 p-5">
        {properties.length === 0 ? (
          <ItemNotFound />
        ) : (
          <>
            <PropertiesList grid={2} properties={properties} />
            {totalPages > 1 && (
              <div className="mt-8">
                <PaginationContainer queryParams={{ page: currentPage }} totalPages={totalPages} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Map Section */}
      {propertyCoordinates.length > 0 && (
        <div
          className="hidden lg:block lg:flex-shrink-0"
          style={{
            width: "50%", // map takes 50% of the screen on large
            minWidth: 400,
            height: mapHeight, // dynamic height matching list
          }}
        >
          <DynamicMap coordinatesList={propertyCoordinates} />
        </div>
      )}
    </section>
  )
}